/* eslint-disable sonarjs/cognitive-complexity */

import { SmlsButton } from '@smiles/smiles-ui-kit-react';
import moment from 'moment';
import React, { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { DayPickerRangeController, FocusedInputShape, isSameDay } from 'react-dates';
import { mapToCssModules } from '../../../utils';
import HeaderMobile from './HeaderMobile';
import { DateHighlightProps, DatePickerWrapperProps } from './types';

interface IDatePickerWrapperProps extends DatePickerWrapperProps {
  showCloseButton?: boolean;
  onSubmit: () => void;
  onClose?: () => void;
  isDayBlocked?: (day: moment.Moment) => boolean;
}

const NewDatePickerWrapper = ({
  showInputs = true,
  startDate,
  endDate,
  onDatesChange,
  onToggle,
  onSubmit,
  onClose = () => undefined,
  isOpen = false,
  isSingleDate = false,
  disabled = false,
  singleDateTitleHeader = 'Data da viagem',
  minimumNights = 1,
  displayFormat = 'ddd, DD MMM',
  datesListHighlight = [],
  startDatePlaceholderText = '',
  endDatePlaceholderText = '',
  statusAnimation = 'paused',
  forceFocus,
  isDayBlocked = () => false,
  isOutsideRange,
  renderMonthElement,
  renderDayContents,
  showCloseButton = true,
}: IDatePickerWrapperProps) => {
  const START_DATE = 'startDate';
  const END_DATE = 'endDate';

  const [focusedInput, setFocusedInput] = useState<FocusedInputShape | null>(START_DATE);
  const containerRef = useRef<any>(null);
  const mobileInputRef = useRef<HTMLInputElement>(null);
  const [isTopScroll, setOnScroll] = useState<boolean>(true);

  const startDateString = startDate && startDate.format('YYYY-MM-DD');
  const endDateString = endDate && endDate.format('YYYY-MM-DD');

  const handleOnFocusChange = (inputChange: FocusedInputShape | null) => {
    if (isSingleDate) {
      setFocusedInput(START_DATE);
      return;
    }
    setFocusedInput(!inputChange ? START_DATE : inputChange);
  };

  const onScroll = (e: SyntheticEvent) => {
    const target = e.target as HTMLTextAreaElement;
    setOnScroll(false);

    if (target.scrollTop === 0) {
      setOnScroll(true);
    }
  };

  const handleClose = () => {
    onClose();
    setFocusedInput(null);
  };

  const closeCalendarMobile = () => {
    if (onToggle) {
      setFocusedInput(null);
      return onToggle(false);
    }
    return undefined;
  };

  const handleSubmit = () => {
    onSubmit();
    closeCalendarMobile();
  };

  const setValue = (): string => {
    let value = '';
    if (startDateString && endDateString && !isOpen) {
      value = `${moment(startDateString || '').format(displayFormat as string)} - ${moment(endDateString).format(
        displayFormat as string,
      )}`;
    }

    if (startDateString && isSingleDate && !isOpen) {
      value = `${moment(startDateString || '').format(displayFormat as string)}`;
    }

    return value;
  };

  useEffect(() => {
    if (!datesListHighlight || datesListHighlight.length === 0) {
      return;
    }

    if (containerRef && containerRef.current) {
      const calendars = containerRef.current.getElementsByClassName('CalendarMonth');

      if (!calendars) {
        return;
      }

      if (!calendars.forEach) {
        calendars.forEach = Array.prototype.forEach;
      }

      calendars.forEach((htmlCalendar: HTMLDivElement, idx: number) => {
        const monthIcon = htmlCalendar.getElementsByClassName('smls-calendar-month-icon');

        if (monthIcon && monthIcon.length) {
          return;
        }

        const monthTextContent = htmlCalendar.getElementsByTagName('strong');

        if (!monthTextContent) {
          return;
        }

        const monthText = monthTextContent[0].textContent || monthTextContent[0].innerText;
        const idxMonth = moment.months().indexOf(monthText.toLocaleLowerCase());
        const idxYear = Math.abs(Math.floor(idx / 12));
        const momentDate = moment(new Date(`${moment().get('year') + idxYear}-${idxMonth + 1}-01`));

        const highlightDays = datesListHighlight.filter(
          (holyday: DateHighlightProps) =>
            holyday.date.get('month') === momentDate.month() && holyday.date.year() === momentDate.year(),
        );

        if (!highlightDays || !highlightDays.length) {
          return;
        }

        const infoPanel = document.createElement('ul', {});
        infoPanel.setAttribute('class', 'smls-calendar-month-icon');
        highlightDays.forEach((day: any) => {
          const li = document.createElement('li', {});
          const span = document.createElement('span', {});
          span.innerText = `${day.date.date().toString()} - ${day.description}`;
          li.appendChild(span);
          infoPanel.appendChild(li);
        });

        htmlCalendar.appendChild(infoPanel);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef.current]);

  useEffect(() => {
    if (forceFocus && mobileInputRef.current) {
      mobileInputRef.current.focus();
    }
  }, [forceFocus, mobileInputRef]);

  return (
    <div
      ref={containerRef}
      onScroll={onScroll}
      className="smls-calendar-main-container"
      style={{
        height: '100%',
        width: '100%',
        position: isOpen ? 'absolute' : 'initial',
        zIndex: 1029,
      }}
    >
      {showInputs && (
        <>
          <div
            className={mapToCssModules([
              'smls-calendar-mobile-input-container',
              !isTopScroll && isOpen ? 'on-scroll-shadow' : '',
            ])}
          >
            <div
              className="smls-calendar-single-date-header-input"
              style={{
                display: isSingleDate && isOpen ? 'flex' : 'none',
              }}
            >
              {singleDateTitleHeader}
            </div>
            <button
              disabled={disabled}
              className={mapToCssModules(['smls-calendar-header-input', disabled ? 'disabled' : ''])}
              style={{
                display: isSingleDate && isOpen ? 'none' : '',
                border: '1px solid red !important',
              }}
              type="button"
              onFocus={() => {
                if (disabled) {
                  return undefined;
                }

                if (onToggle) {
                  return onToggle(true);
                }

                return undefined;
              }}
            >
              <div style={{ textAlign: 'left' }} className={forceFocus && !isOpen ? 'selected' : ''}>
                <button type="button" className="smls-calendar-input-button">
                  <i className="material-icons notranslate" tabIndex={-1}>
                    &#xe878;
                  </i>
                </button>
                <input
                  id="date-mobile"
                  ref={mobileInputRef}
                  type="text"
                  name="startDate"
                  className="singleDate"
                  value={setValue()}
                  onFocus={() => {
                    setFocusedInput(START_DATE);
                    return undefined;
                  }}
                  tabIndex={-1}
                  placeholder={startDatePlaceholderText}
                  readOnly
                />
                <input
                  id="start-date-mobile"
                  type="text"
                  style={{ display: isSingleDate && isOpen ? 'none' : '' }}
                  className={focusedInput === START_DATE ? 'selected' : ''}
                  name="startDate"
                  value={startDateString ? moment(startDateString || '').format(displayFormat as string) : ''}
                  onFocus={() => {
                    setFocusedInput(START_DATE);
                    if (onToggle) {
                      return onToggle(true);
                    }
                    return undefined;
                  }}
                  tabIndex={-1}
                  placeholder={startDatePlaceholderText}
                  readOnly
                />
                <input
                  id="end-date-mobile"
                  type="text"
                  style={{
                    display: isSingleDate && isOpen ? 'none' : '',
                  }}
                  className={focusedInput === END_DATE ? 'selected' : ''}
                  name="endDate"
                  value={endDateString ? moment(endDateString).format(displayFormat as string) : ''}
                  tabIndex={-1}
                  onFocus={() => {
                    if (onToggle) {
                      return onToggle(true);
                    }
                    return undefined;
                  }}
                  placeholder={endDatePlaceholderText}
                  readOnly
                />
              </div>
            </button>
            {showCloseButton && (
              <button type="button" className="smls-calendar-header-button" onClick={handleClose}>
                <i
                  data-testid="close-modal-mobile"
                  className="material-icons notranslate"
                  aria-label="Close"
                  tabIndex={-1}
                >
                  &#xe5cd;
                </i>
              </button>
            )}
            <div
              className="action-panel"
              style={{
                display:
                  (isOpen && endDateString && statusAnimation === 'paused') ||
                  (isOpen && isSingleDate && startDateString && statusAnimation === 'paused')
                    ? 'block'
                    : 'none',
              }}
            >
              <SmlsButton text="Confirmar" className="button-confirm" onClick={handleSubmit} />
            </div>
          </div>
        </>
      )}

      <DayPickerRangeController
        renderMonthElement={(momentMonth) => {
          if (renderMonthElement) {
            return renderMonthElement(momentMonth);
          }
          return <HeaderMobile currentMoment={momentMonth.month} />;
        }}
        renderDayContents={renderDayContents}
        minimumNights={isSingleDate ? 0 : minimumNights}
        isDayBlocked={isDayBlocked}
        isOutsideRange={isOutsideRange}
        isDayHighlighted={(day1) => datesListHighlight.some((day2: DateHighlightProps) => isSameDay(day1, day2.date))}
        enableOutsideDays={false}
        daysViolatingMinNightsCanBeClicked={false}
        orientation="verticalScrollable"
        verticalHeight={800}
        withPortal={false}
        initialVisibleMonth={() => moment()}
        numberOfMonths={13}
        isRTL={false}
        onDatesChange={onDatesChange}
        onFocusChange={handleOnFocusChange}
        focusedInput={focusedInput}
        startDate={startDate || null}
        endDate={endDate || null}
        calendarInfoPosition="bottom"
      />
    </div>
  );
};

NewDatePickerWrapper.defaultProps = {
  showCloseButton: true,
};

export default NewDatePickerWrapper;
